import { useEffect, useMemo, useState } from "react";
import { api, extractError } from "@/lib/apiClient";
import { toast } from "sonner";
import {
  Package, Clock, CheckCircle2, X, Loader2, IndianRupee,
  Play, Users, User, Sparkles, Filter, Radio, ExternalLink,
} from "lucide-react";
import { useI18n } from "@/i18n";

const MODE_LABELS = { self_paced: "Self-paced", live_group: "Live group", one_to_one: "One-to-one" };
const MODE_ICONS = { self_paced: Play, live_group: Users, one_to_one: User };

export default function Therapies() {
  const { t } = useI18n();
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState([]);
  const [packages, setPackages] = useState([]);
  const [owned, setOwned] = useState([]);
  const [filterCat, setFilterCat] = useState(null);
  const [filterMode, setFilterMode] = useState(null);
  const [playing, setPlaying] = useState(null); // therapy obj or null
  const [checkout, setCheckout] = useState(null);
  const [buying, setBuying] = useState(false);

  const refreshItems = () => {
    const p = new URLSearchParams();
    if (filterCat) p.set("category", filterCat);
    if (filterMode) p.set("mode", filterMode);
    api.get(`/datp/therapies?${p.toString()}`).then((r) => setItems(r.data)).catch(() => {});
  };

  useEffect(() => {
    api.get("/datp/categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/care-packages").then((r) => setPackages(r.data)).catch(() => {});
    api.get("/me/packages").then((r) => setOwned(r.data.map((x) => x.package_id))).catch(() => {});
  }, []);
  useEffect(() => { refreshItems(); /* eslint-disable-next-line */ }, [filterCat, filterMode]);

  const catsWithItems = cats.filter((c) => c.published_count > 0);
  const totalCount = cats.reduce((n, c) => n + c.published_count, 0);

  const buy = async () => {
    if (!checkout) return;
    setBuying(true);
    try {
      // Real payment flow — creates a Stripe Checkout session and
      // redirects the user. On return they land on /payment/success
      // which polls status and confirms activation.
      const { data } = await api.post("/payments/checkout", {
        package_id: checkout.id,
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      throw new Error("No checkout URL returned");
    } catch (e) { toast.error(extractError(e)); setBuying(false); }
  };

  return (
    <div className="space-y-10" data-testid="therapies-page">
      <div>
        <span className="uppercase-label">Adjunctive care</span>
        <h1 className="font-display text-3xl mt-2">Therapies &amp; care packages</h1>
        <p className="text-[color:var(--mano-ink-soft)] mt-2 max-w-2xl">
          Evidence-informed wellness content curated to complement your clinical care —
          self-paced videos, live group sessions, and one-to-one therapist support.
        </p>
      </div>

      {/* ============= DATP catalogue ============= */}
      <section data-testid="datp-catalogue" className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-[color:var(--mano-primary)]" />
            <span className="uppercase-label">Therapy library ({totalCount})</span>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-1.5" data-testid="datp-filters">
          <FilterChip active={!filterCat} onClick={() => setFilterCat(null)} testid="chip-cat-all">All</FilterChip>
          {catsWithItems.map((c) => (
            <FilterChip key={c.key} active={filterCat === c.key} onClick={() => setFilterCat(filterCat === c.key ? null : c.key)} testid={`chip-cat-${c.key}`}>
              {c.name.replace("Therapies", "").trim()} <span className="opacity-60">· {c.published_count}</span>
            </FilterChip>
          ))}
        </div>
        <div className="flex flex-wrap gap-1.5">
          <FilterChip small active={!filterMode} onClick={() => setFilterMode(null)}>All modes</FilterChip>
          {Object.entries(MODE_LABELS).map(([v, l]) => (
            <FilterChip key={v} small active={filterMode === v} onClick={() => setFilterMode(filterMode === v ? null : v)} testid={`chip-mode-${v}`}>
              {l}
            </FilterChip>
          ))}
        </div>

        {items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[color:var(--mano-border)] p-8 text-center text-[color:var(--mano-ink-soft)]">
            No therapies published yet. Check back soon — your care team is adding content.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="datp-grid">
            {items.map((t) => {
              const Icon = MODE_ICONS[t.mode] || Play;
              return (
                <button
                  key={t.id}
                  onClick={() => setPlaying(t)}
                  data-testid={`datp-item-${t.id}`}
                  className="text-left rounded-2xl border border-[color:var(--mano-border)] bg-white overflow-hidden hover:border-[color:var(--mano-primary)] transition-colors group"
                >
                  <div className="relative h-32 bg-gradient-to-br from-[color:var(--mano-primary)] to-[color:var(--mano-primary-hover)]">
                    {t.thumbnail_url ? (
                      <img src={t.thumbnail_url} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-white/80">
                        <Icon size={40} />
                      </div>
                    )}
                    <span className="absolute top-2 left-2 text-[10px] font-semibold uppercase bg-white/95 text-[color:var(--mano-primary)] px-2 py-0.5 rounded-full">
                      {MODE_LABELS[t.mode]}
                    </span>
                    {t.duration_min ? (
                      <span className="absolute top-2 right-2 text-[10px] font-semibold bg-black/50 text-white px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                        <Clock size={10} /> {t.duration_min}m
                      </span>
                    ) : null}
                  </div>
                  <div className="p-4">
                    <div className="font-display text-base leading-tight">{t.title}</div>
                    {t.subtitle && <div className="text-xs text-[color:var(--mano-ink-soft)] mt-0.5">{t.subtitle}</div>}
                    <div className="text-[11px] uppercase-label mt-2">{t.category_name}{t.subcategory ? " · " + t.subcategory : ""}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* ============= Care packages (existing) ============= */}
      <section className="space-y-4">
        <div>
          <span className="uppercase-label">Care Packages</span>
          <h2 className="font-display text-2xl mt-1">Structured programmes</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {packages.map((p) => {
            const isOwned = owned.includes(p.id);
            return (
              <div key={p.id} data-testid={`package-card-${p.id}`} className="rounded-3xl border-2 border-[color:var(--mano-primary)] bg-white overflow-hidden">
                <div className="relative">
                  <img src={p.image} alt={p.name} className="h-40 w-full object-cover" />
                  <span className="absolute top-3 left-3 uppercase-label bg-[color:var(--mano-primary)] text-white px-3 py-1 rounded-full">Package</span>
                  {isOwned && (
                    <span className="absolute top-3 right-3 inline-flex items-center gap-1 bg-[color:var(--mano-normal)] text-white px-3 py-1 rounded-full text-xs font-semibold">
                      <CheckCircle2 size={12} /> Owned
                    </span>
                  )}
                </div>
                <div className="p-6">
                  <div className="font-display text-xl">{p.name}</div>
                  <div className="text-sm text-[color:var(--mano-ink-soft)] mt-1">{p.description}</div>
                  <div className="mt-4 space-y-1.5 text-sm">
                    {p.components.map((c, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--mano-primary)]" />
                        {c.label}
                      </div>
                    ))}
                  </div>
                  <div className="mt-5 flex items-center justify-between">
                    <div className="font-display text-2xl inline-flex items-center">
                      <IndianRupee size={20} className="mr-0.5" />{p.price_inr}
                    </div>
                    {isOwned ? (
                      <span className="text-sm font-semibold text-[color:var(--mano-normal)] inline-flex items-center gap-1">
                        <CheckCircle2 size={14} /> Active
                      </span>
                    ) : (
                      <button
                        data-testid={`buy-package-${p.id}`}
                        onClick={() => setCheckout(p)}
                        className="px-5 py-2.5 rounded-full bg-[color:var(--mano-primary)] hover:bg-[color:var(--mano-primary-hover)] text-white text-sm font-semibold transition-colors"
                      >
                        Buy
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Player modal */}
      {playing && <MediaPlayer therapy={playing} onClose={() => setPlaying(null)} />}

      {/* Checkout modal */}
      {checkout && (
        <div data-testid="checkout-modal" className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={() => !buying && setCheckout(null)}>
          <div className="bg-white rounded-3xl w-full max-w-md p-6 lg:p-7" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <span className="uppercase-label">Checkout</span>
                <h3 className="font-display text-2xl mt-1">{checkout.name}</h3>
              </div>
              <button onClick={() => setCheckout(null)} className="p-1" data-testid="checkout-close"><X size={18} /></button>
            </div>
            <div className="mt-4 rounded-2xl border border-[color:var(--mano-border)] p-4 space-y-1.5 text-sm">
              {checkout.components.map((c, i) => (
                <div key={i} className="flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-[color:var(--mano-normal)]" />
                  <span>{c.label}</span>
                </div>
              ))}
            </div>
            <div className="mt-5 flex items-center justify-between">
              <div>
                <div className="uppercase-label">Total</div>
                <div className="font-display text-3xl inline-flex items-center">
                  <IndianRupee size={22} className="mr-0.5" />{checkout.price_inr}
                </div>
              </div>
              <div className="text-xs text-[color:var(--mano-ink-soft)] max-w-[160px] text-right">Secure Stripe checkout · test mode</div>
            </div>
            <button data-testid="checkout-confirm" onClick={buy} disabled={buying} className="mt-5 w-full inline-flex items-center justify-center gap-2 h-12 rounded-full bg-[color:var(--mano-primary)] hover:bg-[color:var(--mano-primary-hover)] text-white font-semibold transition-colors disabled:opacity-60">
              {buying ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
              {buying ? "Redirecting to Stripe…" : "Pay securely with Stripe"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterChip({ active, onClick, children, testid, small = false }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`${small ? "px-3 py-1 text-xs" : "px-3.5 py-1.5 text-sm"} rounded-full font-semibold transition-colors ${
        active
          ? "bg-[color:var(--mano-primary)] text-white"
          : "bg-white border border-[color:var(--mano-border)] text-[color:var(--mano-ink-soft)] hover:border-[color:var(--mano-primary)]/40"
      }`}
    >
      {children}
    </button>
  );
}

function MediaPlayer({ therapy, onClose }) {
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const url = therapy.media_url?.startsWith("/api/") ? `${BACKEND}${therapy.media_url}` : therapy.media_url;
  const type = therapy.media_type;
  const isYouTube = type === "youtube";
  const isVimeo = type === "vimeo";
  const isAudio = type === "audio";
  const isPdf = type === "pdf";
  const [completing, setCompleting] = useState(false);
  const [completed, setCompleted] = useState(false);

  // Record an "open" the first time the player mounts for a therapy.
  useEffect(() => {
    if (!therapy?.id) return;
    api.post(`/datp/therapies/${therapy.id}/open`).catch(() => {});
  }, [therapy?.id]);

  const markDone = async () => {
    setCompleting(true);
    try {
      await api.post(`/datp/therapies/${therapy.id}/complete`);
      setCompleted(true);
      toast.success("Nice — marked as complete");
    } catch (e) { toast.error(extractError(e)); }
    finally { setCompleting(false); }
  };
  const embed = useMemo(() => {
    if (!url) return null;
    if (isYouTube) {
      const m = url.match(/(?:v=|youtu\.be\/)([\w-]{11})/);
      const id = m?.[1];
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
    if (isVimeo) {
      const m = url.match(/vimeo\.com\/(\d+)/);
      const id = m?.[1];
      return id ? `https://player.vimeo.com/video/${id}` : null;
    }
    return null;
  }, [url, isYouTube, isVimeo]);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose} data-testid="datp-player">
      <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 pb-3">
          <div>
            <span className="uppercase-label">{therapy.category_name}{therapy.subcategory ? " · " + therapy.subcategory : ""}</span>
            <h3 className="font-display text-2xl mt-1">{therapy.title}</h3>
            {therapy.subtitle && <div className="text-sm text-[color:var(--mano-ink-soft)] mt-0.5">{therapy.subtitle}</div>}
          </div>
          <button onClick={onClose} className="p-1"><X size={18} /></button>
        </div>

        <div className="bg-black">
          {embed ? (
            <iframe title={therapy.title} src={embed} className="w-full aspect-video" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />
          ) : isAudio ? (
            <div className="p-8 flex items-center justify-center bg-[color:var(--mano-hero)]">
              <audio controls autoPlay src={url} className="w-full max-w-md" />
            </div>
          ) : isPdf ? (
            <iframe title={therapy.title} src={url} className="w-full h-[70vh]" />
          ) : url ? (
            <video controls src={url} className="w-full max-h-[70vh]" />
          ) : (
            <div className="p-10 text-center text-white">No media attached yet.</div>
          )}
        </div>

        <div className="p-5 space-y-3">
          <div className="flex flex-wrap gap-2 text-xs text-[color:var(--mano-ink-soft)] items-center">
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[color:var(--mano-primary)]/10 text-[color:var(--mano-primary)] font-semibold">
              {MODE_LABELS[therapy.mode] || therapy.mode}
            </span>
            {therapy.duration_min ? <span className="inline-flex items-center gap-1"><Clock size={12} /> {therapy.duration_min} min</span> : null}
            <span className="uppercase">{therapy.language}</span>
            <div className="flex-1" />
            {therapy.mode === "live_group" ? (
              <LiveGroupJoin therapy={therapy} />
            ) : (
              <button
                data-testid="mark-complete-btn"
                onClick={markDone}
                disabled={completing || completed}
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  completed
                    ? "bg-emerald-100 text-emerald-800"
                    : "bg-[color:var(--mano-primary)] hover:bg-[color:var(--mano-primary-hover)] text-white"
                } disabled:opacity-60`}
              >
                {completing ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                {completed ? "Completed" : "Mark complete"}
              </button>
            )}
          </div>
          {therapy.description && (
            <p className="text-sm leading-relaxed text-[color:var(--mano-ink)]">{therapy.description}</p>
          )}
          {therapy.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {therapy.tags.map((tag) => (
                <span key={tag} className="text-[10px] uppercase-label bg-[color:var(--mano-bg)] px-2 py-0.5 rounded-full">{tag}</span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function LiveGroupJoin({ therapy }) {
  const scheduled = therapy.scheduled_at ? new Date(therapy.scheduled_at) : null;
  const now = new Date();
  const opensAt = scheduled ? new Date(scheduled.getTime() - 30 * 60 * 1000) : null;
  const closesAt = scheduled ? new Date(scheduled.getTime() + 3 * 60 * 60 * 1000) : null;
  const withinWindow = !scheduled || (now >= opensAt && now <= closesAt);
  const label = scheduled
    ? scheduled.toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
    : "Anytime";

  return (
    <div className="flex items-center gap-2 flex-wrap justify-end">
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[color:var(--mano-ink-soft)]">
        <Radio size={11} /> {label}
      </span>
      <a
        data-testid="join-live-group-btn"
        href={withinWindow ? `/live-group/${therapy.id}` : undefined}
        title={!withinWindow ? "Opens 30 min before the scheduled start" : ""}
        className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
          withinWindow
            ? "bg-[color:var(--mano-primary)] hover:bg-[color:var(--mano-primary-hover)] text-white"
            : "bg-slate-200 text-slate-500 cursor-not-allowed"
        }`}
      >
        <ExternalLink size={12} />
        {withinWindow ? "Join live group" : "Not yet open"}
      </a>
    </div>
  );
}
